import search from "@/data/search.json";
import cardData from "@/data/one_piece/card_data.json";
import { getDonData } from "@/lib/getDonData";
import { OPItem } from "@/interfaces/CardItem";
import { SrcType } from "@/models/alt-arts";
import { AaMap } from "@/models/alt-arts";
import aaJson from "@/data/one_piece/aa_data.json";
import { BaseCardDataManager } from "@/lib/games/base/getCardDataBase";
import { getCardImgproxyUrl } from "@/lib/imgproxy";
import { cardImageUrl } from "@/lib/cdn";

const aa = aaJson as AaMap;

class OnePieceCardDataManager extends BaseCardDataManager<OPItem> {
  protected readonly cdnGame = "one-piece" as const;
  protected readonly imageVersion = "1.1";
  protected readonly defaultRegion = "EN";
  private runtimeCardData: Record<string, any> | null = null;
  private runtimeAAData: Record<string, any> | null = null;

  constructor() {
    super(aa);
  }

  /**
   * Set runtime card data (fetched from API)
   * This takes precedence over static JSON imports
   */
  public setRuntimeCardData(data: Record<string, any>) {
    console.log(
      "[TEST] OnePieceCardDataManager.setRuntimeCardData called with",
      Object.keys(data).length,
      "cards",
    );
    console.log(
      "[TEST] EB05 cards in runtime data:",
      Object.keys(data).filter((id) => id.startsWith("EB05")).length,
    );
    this.runtimeCardData = data;
    // Clear the cached CARD_DATA to force re-cleaning with new runtime data
    this.CARD_DATA = {};
  }

  /**
   * Set runtime AA data (fetched from API)
   * This takes precedence over static JSON imports
   */
  public setRuntimeAAData(data: Record<string, any>) {
    console.log(
      "[TEST] OnePieceCardDataManager.setRuntimeAAData called with",
      Object.keys(data).length,
      "entries",
    );
    this.runtimeAAData = data;
    // Update the AA map used by base class
    this.aa = data;
  }

  protected getCorrectedRegion(region: SrcType | ""): string {
    if (region !== "tcgplayer") {
      return region.toUpperCase().replaceAll("_", "-");
    }
    return "";
  }

  protected clean(data: Record<string, any>): Record<string, OPItem> {
    let cleanedData: Record<string, OPItem> = {};
    for (const [k, v] of Object.entries(data)) {
      // Create a deep copy of the current item
      let item: any = structuredClone(v);

      // Convert life to a number if it's a string
      if (item.life && typeof item.life === "string") {
        item.life = this.getIntFromStr(item.life);
      }

      // Convert cost to a number if it's a string
      if (typeof item.cost === "string") {
        item.cost = this.getIntFromStr(item.cost);
      }

      // Convert power to a number if it's a string
      if (typeof item.power === "string") {
        item.power = this.getIntFromStr(item.power);
      }

      // Convert counter to a number if it's a string
      if (typeof item.counter === "string") {
        item.counter = this.getIntFromStr(item.counter);
      }

      if (!("number" in item)) {
        item["number"] = item.id.split("_p")[0];
      }

      if (item.description === null) {
        item.description = "";
      }

      // Add the cleaned item to the cleanedData object
      cleanedData[k] = item;
    }

    return cleanedData;
  }

  public cleanCardData(): Record<string, OPItem> {
    // Check if CARD_DATA array is not empty
    if (Object.keys(this.CARD_DATA).length === 0) {
      // Use runtime data if available, otherwise fall back to static JSON
      const sourceData = this.runtimeCardData || cardData;
      console.log(
        "[TEST] cleanCardData - using",
        this.runtimeCardData ? "runtime" : "static",
        "data",
      );
      console.log(
        "[TEST] Source data has",
        Object.keys(sourceData).length,
        "cards",
      );
      console.log(
        "[TEST] EB05 cards in source:",
        Object.keys(sourceData).filter((id) => id.startsWith("EB05")).length,
      );
      this.CARD_DATA = this.clean(sourceData);
      console.log(
        "[TEST] After cleaning, CARD_DATA has",
        Object.keys(this.CARD_DATA).length,
        "cards",
      );
    }
    // Return an empty array if CARD_DATA is empty
    return this.CARD_DATA;
  }

  public cleanData(data: Record<string, any>): Record<string, OPItem> {
    return this.clean(data);
  }

  public getCardData(cardId: string): OPItem | undefined {
    if (!cardId) {
      return undefined;
    }

    if (cardId.includes("DON")) {
      return getDonData(cardId.split("-")[1]);
    }

    const cardNumber = cardId.split("_p")[0];
    const allCards = this.cleanCardData();

    // Log EB05 requests to debug
    if (cardNumber.startsWith("EB05")) {
      const card = allCards[cardNumber];
      if (!card) {
        console.log(
          "[TEST] Available EB05 cards:",
          Object.keys(allCards)
            .filter((id) => id.startsWith("EB05"))
            .slice(0, 10),
        );
      }
    }

    return allCards[cardNumber];
  }
}

// Create singleton instance
const onePieceManager = new OnePieceCardDataManager();

// Export functions that delegate to the manager

export function cleanCardData(): Record<string, OPItem> {
  return onePieceManager.cleanCardData();
}

export function clean(data: Record<string, any>): Record<string, OPItem> {
  return onePieceManager.cleanData(data);
}

export function getCardImage(
  cardId: string,
  size: "original" | "316x440" | "158x220" | "90x125" = "original",
  region: SrcType | "" = "",
): string {
  return onePieceManager.getCardImage(cardId, size, region);
}

export function getCardData(cardId: string): OPItem | undefined {
  return onePieceManager.getCardData(cardId);
}

export function getAACardIdData(aaCardId: string): OPItem | undefined {
  return onePieceManager.getAACardIdData(aaCardId);
}

export function getAACardId(aa: any): string {
  return onePieceManager.getAACardId(aa);
}

export function getAAFromCardId(aaCardId: string): any {
  return onePieceManager.getAAFromCardId(aaCardId);
}

export function getAA(cardNumber: string, srcChar: string, count: number): any {
  return onePieceManager.getAA(cardNumber, srcChar, count);
}

export function getAltArts(cardId: string, showAAs: boolean): any[] {
  return onePieceManager.getAltArts(cardId, showAAs);
}

export function setRuntimeCardData(data: Record<string, any>): void {
  return onePieceManager.setRuntimeCardData(data);
}

export function setRuntimeAAData(data: Record<string, any>): void {
  return onePieceManager.setRuntimeAAData(data);
}
